<template>
  <v-data-table
    :headers="headers"
    :items="items"
    :search="search"
    :items-per-page="items.length"
    item-key="id"
    class="budget-table mb-8"
    fixed-header
    calculate-widths
    hide-default-footer
    :style="`--header-height: ${headerHeight}px`"
  >
    <template #item.description="{ item }">
      {{ item.description || "&mdash;" }}
    </template>
    <template #item.accounts="{ item }">
      {{ item.accounts.length }}
    </template>
    <template #item.approver="{ item }">
      <template v-if="item.approvers.length">
        {{ displayApprovers(item.approvers) }}
      </template>

      <svg
        v-else
        v-tooltip="{
          content: 'Approval flow is not set for this budget entity',
          ...standardTooltipProps,
        }"
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
      >
        <mask id="mask0_10351_53376" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="24"
              height="24">
          <rect width="24" height="24" fill="#D9D9D9"/>
        </mask>
        <g mask="url(#mask0_10351_53376)">
          <path
            d="M7.25 12.75H16.75V11.25H7.25V12.75ZM12.0017 21.5C10.6877 21.5 9.45267 21.2507 8.2965 20.752C7.14033 20.2533 6.13467 19.5766 5.2795 18.7218C4.42433 17.8669 3.74725 16.8617 3.24825 15.706C2.74942 14.5503 2.5 13.3156 2.5 12.0017C2.5 10.6877 2.74933 9.45267 3.248 8.2965C3.74667 7.14033 4.42342 6.13467 5.27825 5.2795C6.13308 4.42433 7.13833 3.74725 8.294 3.24825C9.44967 2.74942 10.6844 2.5 11.9983 2.5C13.3123 2.5 14.5473 2.74933 15.7035 3.248C16.8597 3.74667 17.8653 4.42342 18.7205 5.27825C19.5757 6.13308 20.2528 7.13833 20.7518 8.294C21.2506 9.44967 21.5 10.6844 21.5 11.9983C21.5 13.3123 21.2507 14.5473 20.752 15.7035C20.2533 16.8597 19.5766 17.8653 18.7218 18.7205C17.8669 19.5757 16.8617 20.2528 15.706 20.7518C14.5503 21.2506 13.3156 21.5 12.0017 21.5ZM12 20C14.2333 20 16.125 19.225 17.675 17.675C19.225 16.125 20 14.2333 20 12C20 9.76667 19.225 7.875 17.675 6.325C16.125 4.775 14.2333 4 12 4C9.76667 4 7.875 4.775 6.325 6.325C4.775 7.875 4 9.76667 4 12C4 14.2333 4.775 16.125 6.325 17.675C7.875 19.225 9.76667 20 12 20Z"
            fill="#62646A"/>
        </g>
      </svg>
    </template>
    <template #item.balance="{ item }">
      <v-row dense class="py-2">
        <v-col cols="6" xl="4" class="lato-bold">
          Balance:
        </v-col>
        <v-col cols="6" xl="8" :class="{ 'green4': item.balance > 0, 'red1': item.balance < 0 }">
          {{
            item.balance | priceFilter({
              style: "currency",
              currency: "USD",
              minimumFractionDigits: 2
            })
          }}
        </v-col>
        <v-col cols="6" xl="4" class="lato-bold">
          On hold:
        </v-col>
        <v-col cols="6" xl="8">
          {{
            item.on_hold | priceFilter({
              style: "currency",
              currency: "USD",
              minimumFractionDigits: 2
            })
          }}
        </v-col>
      </v-row>
    </template>
    <template #item.status="{ item }">
      <update-status-sheet
        :color="item.active ? '#D8F1E4' : '#FFF1FD'"
        :text-color="item.active ? '#005257' : '#8D0377'"
      >
        {{ item.active ? "Active" : "Disabled" }}
      </update-status-sheet>
    </template>
    <template #item.auto_reload="{ item }">
      <div
        class="budget-table__icon-wrapper"
        v-tooltip="{
          content: item.is_auto_reload_enabled
            ? 'Auto-reload set for this budget entity'
            : 'Auto-reload is not set for budget entity',
          ...standardTooltipProps,
          offset: 0
        }"
      >
        <icons-check-circle
          v-if="item.is_auto_reload_enabled"
          :height="19"
          :width="19"
          :check-stroke-width="1.6"
          filled
        />

        <svg v-else xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
          <mask id="mask0_10351_53376" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="24"
                height="24">
            <rect width="24" height="24" fill="#D9D9D9"/>
          </mask>
          <g mask="url(#mask0_10351_53376)">
            <path
              d="M7.25 12.75H16.75V11.25H7.25V12.75ZM12.0017 21.5C10.6877 21.5 9.45267 21.2507 8.2965 20.752C7.14033 20.2533 6.13467 19.5766 5.2795 18.7218C4.42433 17.8669 3.74725 16.8617 3.24825 15.706C2.74942 14.5503 2.5 13.3156 2.5 12.0017C2.5 10.6877 2.74933 9.45267 3.248 8.2965C3.74667 7.14033 4.42342 6.13467 5.27825 5.2795C6.13308 4.42433 7.13833 3.74725 8.294 3.24825C9.44967 2.74942 10.6844 2.5 11.9983 2.5C13.3123 2.5 14.5473 2.74933 15.7035 3.248C16.8597 3.74667 17.8653 4.42342 18.7205 5.27825C19.5757 6.13308 20.2528 7.13833 20.7518 8.294C21.2506 9.44967 21.5 10.6844 21.5 11.9983C21.5 13.3123 21.2507 14.5473 20.752 15.7035C20.2533 16.8597 19.5766 17.8653 18.7218 18.7205C17.8669 19.5757 16.8617 20.2528 15.706 20.7518C14.5503 21.2506 13.3156 21.5 12.0017 21.5ZM12 20C14.2333 20 16.125 19.225 17.675 17.675C19.225 16.125 20 14.2333 20 12C20 9.76667 19.225 7.875 17.675 6.325C16.125 4.775 14.2333 4 12 4C9.76667 4 7.875 4.775 6.325 6.325C4.775 7.875 4 9.76667 4 12C4 14.2333 4.775 16.125 6.325 17.675C7.875 19.225 9.76667 20 12 20Z"
              fill="#62646A"/>
          </g>
        </svg>
      </div>
    </template>
    <template #item.details="{item}">
      <v-menu
        max-width="210"
        open-on-click
        offset-y
        top
        left
        z-index="4"
        nudge-top="8"
        content-class="common-menu__content"
      >
        <template #activator="{ on, attrs }">
          <div
            class="common-menu__activator"
            v-bind="attrs"
            v-on="on"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="4"
              height="16"
              viewBox="0 0 4 16"
              fill="none"
            >
              <path
                d="M2 16C1.45 16 0.979002 15.804 0.587002 15.412C0.195002 15.02 -0.000664969 14.5493 1.69779e-06 14C1.69779e-06 13.45 0.196002 12.979 0.588002 12.587C0.980002 12.195 1.45067 11.9993 2 12C2.55 12 3.021 12.196 3.413 12.588C3.805 12.98 4.00067 13.4507 4 14C4 14.55 3.804 15.021 3.412 15.413C3.02 15.805 2.54934 16.0007 2 16ZM2 10C1.45 10 0.979002 9.804 0.587002 9.412C0.195002 9.02 -0.000664969 8.54934 1.69779e-06 8C1.69779e-06 7.45 0.196002 6.979 0.588002 6.587C0.980002 6.195 1.45067 5.99934 2 6C2.55 6 3.021 6.196 3.413 6.588C3.805 6.98 4.00067 7.45067 4 8C4 8.55 3.804 9.021 3.412 9.413C3.02 9.805 2.54934 10.0007 2 10ZM2 4C1.45 4 0.979002 3.804 0.587002 3.412C0.195002 3.02 -0.000664969 2.54934 1.69779e-06 2C1.69779e-06 1.45 0.196002 0.979002 0.588002 0.587002C0.980002 0.195002 1.45067 -0.000664969 2 1.69779e-06C2.55 1.69779e-06 3.021 0.196001 3.413 0.588001C3.805 0.980001 4.00067 1.45067 4 2C4 2.55 3.804 3.021 3.412 3.413C3.02 3.805 2.54934 4.00067 2 4Z"
                fill="#62646A"
              />
            </svg>
          </div>
        </template>

        <v-list>
          <v-list-item
            :ripple="false"
            @click="$emit('showEditBudgetEntityPanel', item)"
          >
            <v-list-item-icon>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <mask
                  id="mask0_10114_53331"
                  style="mask-type:alpha"
                  maskUnits="userSpaceOnUse"
                  x="0"
                  y="0"
                  width="24"
                  height="24"
                >
                  <rect width="24" height="24" fill="currentColor"/>
                </mask>
                <g mask="url(#mask0_10114_53331)">
                  <path
                    d="M9.69225 21.5L9.3115 18.4538C9.04367 18.3641 8.769 18.2385 8.4875 18.077C8.20617 17.9153 7.95459 17.7422 7.73275 17.5577L4.9115 18.75L2.604 14.75L5.04425 12.9058C5.02125 12.7571 5.00492 12.6077 4.99525 12.4578C4.98559 12.3078 4.98075 12.1583 4.98075 12.0095C4.98075 11.8673 4.98559 11.7228 4.99525 11.576C5.00492 11.4292 5.02125 11.2686 5.04425 11.0943L2.604 9.25L4.9115 5.26925L7.723 6.452C7.96417 6.261 8.22159 6.08633 8.49525 5.928C8.76892 5.76967 9.03784 5.64242 9.302 5.54625L9.69225 2.5H14.3078L14.6885 5.55575C14.9885 5.66475 15.2599 5.792 15.5028 5.9375C15.7458 6.083 15.991 6.2545 16.2385 6.452L19.0885 5.26925L21.396 9.25L18.9173 11.123C18.9531 11.2845 18.9727 11.4355 18.976 11.576C18.9792 11.7163 18.9808 11.8577 18.9808 12C18.9808 12.1358 18.9775 12.274 18.971 12.4145C18.9647 12.5548 18.9417 12.7154 18.902 12.8963L21.3615 14.75L19.0538 18.75L16.2385 17.548C15.991 17.7455 15.7384 17.9202 15.4808 18.072C15.2231 18.224 14.959 18.3481 14.6885 18.4443L14.3078 21.5H9.69225ZM11 20H12.9655L13.325 17.3212C13.8353 17.1879 14.3017 16.9985 14.724 16.753C15.1465 16.5073 15.5539 16.1916 15.9463 15.8057L18.4308 16.85L19.4155 15.15L17.2463 13.5155C17.3296 13.2565 17.3863 13.0026 17.4163 12.7537C17.4464 12.5051 17.4615 12.2538 17.4615 12C17.4615 11.7397 17.4464 11.4884 17.4163 11.2463C17.3863 11.0039 17.3296 10.7564 17.2463 10.5038L19.4345 8.85L18.45 7.15L15.9365 8.2095C15.6018 7.85183 15.2009 7.53583 14.7338 7.2615C14.2664 6.98717 13.7937 6.79292 13.3155 6.67875L13 4H11.0155L10.6845 6.66925C10.1743 6.78975 9.70325 6.97433 9.27125 7.223C8.83909 7.47183 8.42684 7.79233 8.0345 8.1845L5.55 7.15L4.5655 8.85L6.725 10.4595C6.64167 10.6968 6.58334 10.9437 6.55 11.2C6.51667 11.4563 6.5 11.7262 6.5 12.0095C6.5 12.2698 6.51667 12.525 6.55 12.775C6.58334 13.025 6.6385 13.2718 6.7155 13.5155L4.5655 15.15L5.55 16.85L8.025 15.8C8.4045 16.1897 8.81025 16.5089 9.24225 16.7578C9.67442 17.0064 10.152 17.1974 10.675 17.3307L11 20ZM12.0115 15C12.8435 15 13.5515 14.708 14.1355 14.124C14.7195 13.54 15.0115 12.832 15.0115 12C15.0115 11.168 14.7195 10.46 14.1355 9.876C13.5515 9.292 12.8435 9 12.0115 9C11.1692 9 10.4586 9.292 9.87975 9.876C9.30092 10.46 9.0115 11.168 9.0115 12C9.0115 12.832 9.30092 13.54 9.87975 14.124C10.4586 14.708 11.1692 15 12.0115 15Z"
                    fill="currentColor"
                  />
                </g>
              </svg>
            </v-list-item-icon>
            <v-list-item-title>
              Settings
            </v-list-item-title>
          </v-list-item>
          <v-list-item
            :ripple="false"
            @click="$emit('autoReloadEntity', item)"
          >
            <v-list-item-icon>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="24"
                height="24"
                fill="none"
              >
                <mask
                  id="mask0_10114_53311"
                  style="mask-type:alpha"
                  maskUnits="userSpaceOnUse"
                  x="0"
                  y="0"
                  width="24"
                  height="24"
                >
                  <rect width="24" height="24" fill="currentColor"/>
                </mask>
                <g mask="url(#mask0_10114_53311)">
                  <path
                    d="M5.16725 15.6269C4.86475 15.0641 4.63625 14.483 4.48175 13.8837C4.32725 13.2842 4.25 12.6729 4.25 12.0499C4.25 9.88711 5.00258 8.04644 6.50775 6.52794C8.01292 5.00928 9.84367 4.24994 12 4.24994H12.7808L10.9308 2.39994L11.9845 1.34619L15.6385 4.99994L11.9845 8.65369L10.9308 7.59994L12.7808 5.74994H12C10.2628 5.74994 8.78683 6.36144 7.572 7.58444C6.35733 8.80761 5.75 10.2961 5.75 12.0499C5.75 12.4641 5.79517 12.8779 5.8855 13.2912C5.976 13.7047 6.11158 14.1083 6.29225 14.5019L5.16725 15.6269ZM12.0155 22.6537L8.3615 18.9999L12.0155 15.3462L13.0693 16.3999L11.2192 18.2499H12C13.7372 18.2499 15.2132 17.6384 16.428 16.4154C17.6427 15.1923 18.25 13.7038 18.25 11.9499C18.25 11.5358 18.2048 11.122 18.1145 10.7087C18.024 10.2952 17.8884 9.89161 17.7078 9.49794L18.8328 8.37294C19.1353 8.93577 19.3637 9.51686 19.5182 10.1162C19.6727 10.7157 19.75 11.3269 19.75 11.9499C19.75 14.1128 18.9974 15.9534 17.4923 17.4719C15.9871 18.9906 14.1563 19.7499 12 19.7499H11.2192L13.0693 21.5999L12.0155 22.6537Z"
                    fill="currentColor"
                  />
                </g>
              </svg>
            </v-list-item-icon>
            <v-list-item-title>
              Auto reload
            </v-list-item-title>
          </v-list-item>
          <v-list-item
            :ripple="false"
            @click="$emit('addAutomation', item)">
            <v-list-item-icon>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M4.50003 20.5V15.9787C4.50003 15.4813 4.67704 15.0561 5.03106 14.7029C5.38506 14.3497 5.81061 14.1731 6.30773 14.1731H17.6923C18.1894 14.1731 18.615 14.3501 18.969 14.7041C19.323 15.0581 19.5 15.4836 19.5 15.9808V20.5H4.50003ZM9.14426 12.7884C7.85935 12.7884 6.7641 12.3356 5.85848 11.43C4.95285 10.5244 4.50003 9.42911 4.50003 8.14423C4.50003 6.85933 4.95285 5.76407 5.85848 4.85845C6.7641 3.95282 7.85935 3.5 9.14426 3.5H14.8558C16.1407 3.5 17.2359 3.95282 18.1415 4.85845C19.0472 5.76407 19.5 6.85933 19.5 8.14423C19.5 9.42911 19.0472 10.5244 18.1415 11.43C17.2359 12.3356 16.1407 12.7884 14.8558 12.7884H9.14426ZM6.00001 19H18V15.9808C18 15.891 17.9712 15.8173 17.9135 15.7596C17.8558 15.7019 17.782 15.673 17.6923 15.673H6.30773C6.21798 15.673 6.14426 15.7019 6.08656 15.7596C6.02886 15.8173 6.00001 15.891 6.00001 15.9808V19ZM9.14426 11.2885H14.8558C15.734 11.2885 16.4776 10.984 17.0865 10.375C17.6955 9.76602 18 9.02243 18 8.14423C18 7.26601 17.6955 6.52242 17.0865 5.91345C16.4776 5.30447 15.734 4.99998 14.8558 4.99998H9.14426C8.26604 4.99998 7.52245 5.30447 6.91348 5.91345C6.3045 6.52242 6.00001 7.26601 6.00001 8.14423C6.00001 9.02243 6.3045 9.76602 6.91348 10.375C7.52245 10.984 8.26604 11.2885 9.14426 11.2885ZM9.14391 9.0096C9.38901 9.0096 9.59456 8.9267 9.76058 8.7609C9.92661 8.5951 10.0096 8.38965 10.0096 8.14455C10.0096 7.89947 9.92673 7.69391 9.76093 7.52788C9.59513 7.36186 9.38968 7.27885 9.14458 7.27885C8.8995 7.27885 8.69394 7.36175 8.52791 7.52755C8.36189 7.69335 8.27888 7.89879 8.27888 8.14388C8.27888 8.38898 8.36178 8.59453 8.52758 8.76055C8.69338 8.92658 8.89882 9.0096 9.14391 9.0096ZM14.8554 9.0096C15.1005 9.0096 15.3061 8.9267 15.4721 8.7609C15.6381 8.5951 15.7211 8.38965 15.7211 8.14455C15.7211 7.89947 15.6382 7.69391 15.4724 7.52788C15.3066 7.36186 15.1012 7.27885 14.8561 7.27885C14.611 7.27885 14.4054 7.36175 14.2394 7.52755C14.0734 7.69335 13.9904 7.89879 13.9904 8.14388C13.9904 8.38898 14.0733 8.59453 14.2391 8.76055C14.4049 8.92658 14.6103 9.0096 14.8554 9.0096Z"
                  fill="#62646A"/>
              </svg>
            </v-list-item-icon>
            <v-list-item-title>
              {{ getManageAutomationLabel(item) }}
            </v-list-item-title>
          </v-list-item>
          <v-list-item
            v-if="hasAutomation(item)"
            :ripple="false"
            @click="$emit('spendLimitReport', item)">
            <v-list-item-icon class="ml-1">
              <MoneyDocumentIcon :height="20" :width="20"/>
            </v-list-item-icon>
            <v-list-item-title>
              Spend limit report
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </template>
  </v-data-table>
</template>

<script>
  import {budgetTableHeaders} from "@/assets/headers"

  import getHeaderHeight from "@/mixins/getHeaderHeight"
  import {items} from "./mixins"
  import standardTooltipProps from "@/mixins/standardTooltipProps"

  import UpdateStatusSheet from "@/components/UpdateStatusSheet"
  import MoneyDocumentIcon from "@/components/icons/MoneyDocumentIcon.vue"

  export default {
    name: "TheBudgetTable",
    mixins: [
      items,
      getHeaderHeight,
      standardTooltipProps,
    ],
    components: { MoneyDocumentIcon, UpdateStatusSheet },
    props: {
      search: {
        type: String,
        required: false,
        default: null
      }
    },
    data: () => ({
      headers: budgetTableHeaders,
    }),
    methods: {
      displayApprovers(approvers) {
        if (approvers.length) {
          return approvers.length > 2
            ? `${approvers[0].name}, ${approvers[1].name} (+${approvers.length - 2})`
            : approvers.map(item => item.name).join(", ")
        } else {
          return "-"
        }
      },
      hasAutomation(item) {
        return item.automation !== null
      },
      getManageAutomationLabel(item) {
        return this.hasAutomation(item) ? "Edit funding automation" : "Add automation"
      },
    }
  }
</script>

<style lang="scss">
  .budget-table {
    table tr td {
      min-height: 70px !important;
      height: 70px !important;
    }

    tr th {
      border-right: none !important;
      top: calc(var(--header-height, 160px) - 2px) !important;

      i {
        opacity: 1 !important;
        width: 24px !important;
      }
    }

    tbody tr {
      &:nth-child(even) {
        background-color: unset;
      }

      &:hover {
        background-color: #fafafa !important;
      }
    }

    @media (min-width: 1904px) {
      table tr {
        & td, & th {
          // name
          &:nth-last-child(8) {
            min-width: 90px !important;
            width: 90px !important;
          }

          // users
          &:nth-last-child(6) {
            min-width: 55px !important;
            width: 55px !important;
          }

          // approvers
          &:nth-last-child(5) {
            min-width: 150px !important;
            width: 150px !important;
          }

          // credits
          &:nth-last-child(4) {
            min-width: 100px !important;
            width: 100px !important;
          }

          // status
          &:nth-last-child(3) {
            min-width: 74px !important;
            width: 74px !important;
          }

          &:nth-last-child(2) {
            min-width: 50px !important;
            width: 50px !important;
          }

          &:nth-last-child(1) {
            min-width: 30px !important;
            width: 30px !important;
          }
        }
      }
    }

    &__icon-wrapper {
      display: flex;
      padding: 10px;
      width: fit-content;
      margin-inline: auto;
    }
  }
</style>
